import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Spin, Tag, Typography } from "antd";
import { RocketOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AIInsightsQuotaPanel from "./AIInsightsQuotaPanel";

const { Text, Paragraph } = Typography;

const DashboardAIInsightsModal = ({
  visible,
  onClose,
  onSubmit,
  reviewCount,
  reviewCountLoading,
  filterLabels = [],
  quota = null,
  quotaLoading = false,
  quotaError = false,
  isDark = false,
}) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (!visible) {
      setShowConfirmModal(false);
      setSubmitting(false);
    }
  }, [visible]);

  const hasFilters = filterLabels.length > 0;
  const reviewCountMessage = useMemo(() => {
    if (reviewCountLoading) {
      return t("common.aiInsightsCountLoading");
    }
    if (reviewCount !== null) {
      return t("common.aiInsightsCountWithActiveFilters", {
        count: reviewCount,
      });
    }
    return t("common.aiInsightsCountUnavailable");
  }, [reviewCount, reviewCountLoading, t]);

  const suppressReviewCountRow =
    !quotaError && (quotaLoading || quota);

  const thisRun =
    reviewCount ??
    quota?.required_amount ??
    quota?.total_review_count ??
    null;

  const quotaBlocked =
    quota &&
    quota.unlimited !== true &&
    typeof quota.current_quota === "number" &&
    thisRun != null &&
    thisRun > quota.current_quota;

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    setSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <RocketOutlined />
            {t("common.aiInsightsTitle")}
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="cancel" onClick={onClose} disabled={submitting}>
            {t("common.cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => setShowConfirmModal(true)}
            loading={submitting}
            disabled={quotaBlocked || submitting}
            icon={<RocketOutlined />}
          >
            {t("common.aiInsightsGenerate")}
          </Button>,
        ]}
        width={640}
        destroyOnClose
        zIndex={1050}
        maskClosable={false}
      >
        <div
          style={{
            display: "grid",
            gap: "16px",
            color: isDark ? "rgba(255, 255, 255, 0.88)" : undefined,
          }}
        >
          <Paragraph
            style={{
              marginBottom: 0,
              color: isDark ? "rgba(255, 255, 255, 0.88)" : undefined,
            }}
          >
            {t("common.aiInsightsDashboardFiltersInfo")}
          </Paragraph>
          {hasFilters && (
            <div>
              <div
                style={{
                  marginBottom: "8px",
                  fontWeight: 500,
                  color: isDark ? "rgba(255, 255, 255, 0.9)" : undefined,
                }}
              >
                {t("common.aiInsightsAppliedFilters")}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {filterLabels.map((label) => (
                  <Tag
                    key={label}
                    style={{
                      borderRadius: "16px",
                      padding: "4px 12px",
                      marginInlineEnd: 0,
                    }}
                  >
                    {label}
                  </Tag>
                ))}
              </div>
            </div>
          )}
          <AIInsightsQuotaPanel
            quota={quota}
            loading={quotaLoading}
            error={quotaError}
            thisRunOverride={reviewCount}
            isDark={isDark}
          />
          {!suppressReviewCountRow ? (
            <div style={{ minHeight: "24px" }}>
              {reviewCountLoading ? <Spin size="small" /> : null}{" "}
              <Text style={{ color: isDark ? "rgba(255,255,255,0.85)" : undefined }}>{reviewCountMessage}</Text>
            </div>
          ) : null}
          <Paragraph
            type="secondary"
            style={{
              marginBottom: 0,
              color: isDark ? "rgba(255, 255, 255, 0.58)" : undefined,
            }}
          >
            {t("common.aiInsightsQuotaInfo")}
          </Paragraph>
        </div>
      </Modal>
      <Modal
        title={t("common.aiInsightsConfirmTitle")}
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowConfirmModal(false)}>
            {t("common.aiInsightsConfirmCancel")}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleConfirmSubmit}
            loading={submitting}
            icon={<RocketOutlined />}
          >
            {t("common.aiInsightsConfirmButton")}
          </Button>,
        ]}
        width={460}
        zIndex={1060}
        maskClosable={false}
      >
        <p>
          {thisRun != null
            ? t("common.aiInsightsConfirmMessage", { count: thisRun })
            : t("common.aiInsightsConfirmMessageUnavailable")}
        </p>
      </Modal>
    </>
  );
};

export default DashboardAIInsightsModal;
